/**
 * The photo pipeline (sync protocol §4, ADR-0007).
 *
 * Photos are a separate queue, never on the item-save path. Capture resizes the
 * image locally, stores both blobs, and records a `photo.added` event -- all
 * without a network. The uploader catches up afterwards, and the item is fully
 * usable throughout.
 *
 * The derivative is uploaded before the original, deliberately: it is what every
 * other device displays, so sending the small one first makes the photo appear
 * elsewhere in seconds rather than after a multi-megabyte transfer.
 */
import {
  anyEvent,
  photoFetchResponse,
  photoObjectKey,
  PRESIGN_DOWNLOAD_MAX,
  presignUploadResponse,
  uuidv7,
  type AnyEvent,
  type PhotoContentType,
  type PhotoFetchResponse,
  type PresignedPost,
  type PresignUploadResponse,
} from '@intentionalist/shared';
import { ApiError, apiFetch } from './api.js';
import { deviceId, tick } from './clock.js';
import { isSupportedImage, makeDerivative, originalContentType } from './image.js';
import type { ReplicaDb } from './db.js';

/** Keys for the two blobs a photo holds locally. */
export const blobKey = {
  original: (photoId: string): string => `original:${photoId}`,
  derivative: (photoId: string): string => `derivative:${photoId}`,
} as const;

export class PhotoError extends Error {}

/**
 * Prepares a photo for an item, without touching the network.
 *
 * Returns the event to write. The caller passes it to the engine like any other
 * edit, so the photo joins the same outbox and the same ordering as everything
 * else -- there is no second sync path to keep correct.
 */
export async function capture(params: {
  db: ReplicaDb;
  userId: string;
  itemId: string;
  file: File | Blob;
}): Promise<{ photoId: string; event: AnyEvent }> {
  const { db, userId, itemId, file } = params;

  if (!isSupportedImage(file.type)) {
    throw new PhotoError('That file is not a kind of image this app can store.');
  }

  const photoId = uuidv7();
  const derivative = await makeDerivative(file);

  // Both blobs first, then the event. If the process dies between them the worst
  // case is an orphaned blob, which costs space; the reverse would be an event
  // referring to bytes that were never kept.
  await db.transaction('rw', db.photoBlobs, async () => {
    await db.photoBlobs.put({ key: blobKey.derivative(photoId), blob: derivative.blob });
    await db.photoBlobs.put({ key: blobKey.original(photoId), blob: file });
  });

  const at = Date.now();
  const event = anyEvent.parse({
    eventId: uuidv7(at),
    hlc: tick(at),
    deviceId: deviceId(),
    entityId: itemId,
    clientTs: new Date(at).toISOString(),
    type: 'photo.added',
    payload: {
      photoId,
      // Derived locally from the same formula the server uses, which is what lets
      // capture work with no connection at all.
      originalKey: photoObjectKey.original(userId, photoId),
      derivativeKey: photoObjectKey.derivative(userId, photoId),
      width: derivative.width,
      height: derivative.height,
      byteSize: derivative.blob.size,
    },
  });

  return { photoId, event };
}

/** `photo.uploaded`, once both objects are in S3. */
export function uploadedEvent(itemId: string, photoId: string): AnyEvent {
  const at = Date.now();
  return anyEvent.parse({
    eventId: uuidv7(at),
    hlc: tick(at),
    deviceId: deviceId(),
    entityId: itemId,
    clientTs: new Date(at).toISOString(),
    type: 'photo.uploaded',
    payload: { photoId },
  });
}

/**
 * Fetches a photo the user named by URL, through the service (FR-2.17).
 *
 * The service does the fetching because the browser cannot: an image on another
 * origin needs that host to send CORS headers, and most do not. What comes back
 * is bytes and a type, which become a `Blob` and go through `capture` like a
 * file chosen from disk -- so there is one way a photo enters the system, and
 * one place that decides what is stored.
 *
 * The service's refusal messages are written for a person and are shown as they
 * are. It deliberately will not say why an address was refused beyond the two
 * cases a user can act on, so there is nothing to translate here.
 */
export async function fetchByUrl(url: string): Promise<Blob> {
  let response: PhotoFetchResponse;
  try {
    response = photoFetchResponse.parse(
      await apiFetch<unknown>('/photos/fetch', {
        method: 'POST',
        body: JSON.stringify({ url }),
      }),
    );
  } catch (caught) {
    if (caught instanceof ApiError) throw new PhotoError(caught.message);
    throw new PhotoError('That image could not be fetched.');
  }

  const binary = atob(response.bytes);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: response.contentType });
}

async function presign(params: {
  photoId: string;
  itemId: string;
  contentType: PhotoContentType;
  originalContentType: PhotoContentType;
}): Promise<PresignUploadResponse> {
  return presignUploadResponse.parse(
    await apiFetch<unknown>('/photos/presign-upload', {
      method: 'POST',
      body: JSON.stringify(params),
    }),
  );
}

/**
 * Sends one blob to S3 using a signed POST form.
 *
 * The size check is a courtesy, not the enforcement: S3 rejects an oversized body
 * from the `content-length-range` condition in the signed policy regardless, but
 * it answers with a bare 403 that says nothing a person could act on. Checking
 * first buys a message worth showing and saves uploading bytes that are doomed.
 *
 * A blob whose size is not a number is therefore uploaded rather than refused --
 * the policy will decide. Exported so that can be tested without a round trip
 * through IndexedDB, which does not preserve `Blob.size` in every environment.
 */
export async function postBlob(target: PresignedPost, blob: Blob): Promise<void> {
  if (typeof blob.size === 'number' && blob.size > target.maxBytes) {
    throw new PhotoError('That photo is too large to store.');
  }

  const form = new FormData();
  for (const [field, value] of Object.entries(target.fields)) form.append(field, value);
  // The file must be appended last: S3 ignores any field that follows it.
  form.append('file', blob);

  const response = await fetch(target.url, { method: 'POST', body: form });
  if (!response.ok) {
    throw new PhotoError('That photo could not be stored. It will be retried.');
  }
}

export interface UploadOutcome {
  readonly photoId: string;
  readonly event: AnyEvent | null;
  readonly error: string | null;
}

/**
 * Uploads one captured photo.
 *
 * Derivative first, then the original. A failure between the two leaves the photo
 * viewable everywhere while the original is retried, which is the right way round
 * to fail.
 */
export async function upload(params: {
  db: ReplicaDb;
  itemId: string;
  photoId: string;
  contentType?: PhotoContentType;
}): Promise<UploadOutcome> {
  const { db, itemId, photoId } = params;

  const derivativeBlob = await db.photoBlobs.get(blobKey.derivative(photoId));
  const originalBlob = await db.photoBlobs.get(blobKey.original(photoId));

  if (derivativeBlob === undefined) {
    // The blobs were evicted, or this photo arrived from another device and was
    // never captured here. Either way there is nothing local to send.
    return { photoId, event: null, error: null };
  }

  try {
    const signed = await presign({
      photoId,
      itemId,
      contentType: params.contentType ?? 'image/jpeg',
      originalContentType:
        originalBlob === undefined ? 'image/jpeg' : originalContentType(originalBlob.blob),
    });

    await postBlob(signed.derivative, derivativeBlob.blob);
    if (originalBlob !== undefined) {
      await postBlob(signed.original, originalBlob.blob);
      // The original is only needed for export and is the larger of the two, so
      // it is dropped once S3 has it. The derivative stays as the local cache
      // that data model §6 describes.
      await db.photoBlobs.delete(blobKey.original(photoId));
    }

    return { photoId, event: uploadedEvent(itemId, photoId), error: null };
  } catch (error) {
    return {
      photoId,
      event: null,
      error: error instanceof Error ? error.message : 'That photo could not be stored.',
    };
  }
}

/** A local derivative, for display before or instead of a round trip to S3. */
export async function localDerivative(db: ReplicaDb, photoId: string): Promise<Blob | null> {
  return (await db.photoBlobs.get(blobKey.derivative(photoId)))?.blob ?? null;
}

/**
 * Signed URLs for photos this device has no local copy of.
 *
 * Batched, so a scrolling grid costs one round trip rather than one per photo,
 * and paged because the endpoint caps a request at `PRESIGN_DOWNLOAD_MAX`. The
 * paging lives here rather than at the call site: a grid never asks for more
 * than a screenful, but an export asks for every photo in the catalog, and
 * one of the two would otherwise have silently lost everything past the cap.
 */
export async function downloadUrls(keys: readonly string[]): Promise<Record<string, string>> {
  const urls: Record<string, string> = {};

  for (let from = 0; from < keys.length; from += PRESIGN_DOWNLOAD_MAX) {
    const page = keys.slice(from, from + PRESIGN_DOWNLOAD_MAX);
    const body = (await apiFetch<{ urls: Record<string, string> }>('/photos/presign-download', {
      method: 'POST',
      body: JSON.stringify({ keys: page }),
    })) as { urls: Record<string, string> };
    Object.assign(urls, body.urls);
  }
  return urls;
}
